import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a stock availability indicator (dot + text)
 * for the closest product's selected variant, and updates itself in place -
 * without a page reload - whenever the shopper picks a different variant.
 *
 * Mirrors the update pattern used by the theme's built-in `product-inventory`
 * component: it listens for the section's product-select event, waits for
 * the server-rendered replacement HTML, and morphs its own children with the
 * freshly rendered `<stock-availability>` markup for the new variant.
 */
class StockAvailability extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event by updating the stock availability display.
   * @param {CustomEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newAvailability = html.querySelector('stock-availability');
        if (!newAvailability) return;

        morph(this, newAvailability, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[stock-availability] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('stock-availability')) {
  customElements.define('stock-availability', StockAvailability);
}
